import { c as _c } from "react/compiler-runtime";
import { getErrorCode, postHostedOnboardingChallengeMutation, postHostedOnboardingChallengeOptions, postHostedOnboardingChallengeVerifyMutation } from '@onefootprint/fetch-hosted';
import { useRequestError } from '@onefootprint/fetch-hosted/use-request-error';
import type { ApiError, OnboardingChallengeResponse } from '@onefootprint/request-types';
import { useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSessionExpired } from '@/idv2/features/identify/handle-session-expired';
import { OtpChallenge } from '@/idv2/features/identify/otp-challenge';
import { useFootprint } from '@/idv2/hooks/use-footprint';
const SUCCESS_EVENT_DELAY_MS = 1500;
const getRetryAfterDate = (seconds: number) => new Date(Date.now() + seconds * 1000);
export const Route = createFileRoute('/onboarding/$token/register-auth-method')({
  component: RegisterAuthMethodPage,
  loader: async ({
    context: {
      currentRequirement,
      queryClient
    },
    params: {
      token
    }
  }) => {
    if (currentRequirement?.kind !== 'register_auth_method') {
      throw new Error(`Invalid requirement kind: ${currentRequirement?.kind ?? 'undefined'}`);
    }
    const challengeKind = currentRequirement.authMethodKind === 'email' ? 'email' : 'sms';
    const headers = {
      'X-Fp-Authorization': token
    };
    let challengeData: OnboardingChallengeResponse | undefined;
    let initialRetryDisabledUntil: Date | undefined;
    try {
      // Always re-issue the challenge on entry rather than replaying a cached one: the user can go
      // back, edit their phone/email, and return here with the same token, so a cached challenge
      // would have been sent to the old contact info. fetchQuery re-POSTs; ensureQueryData wouldn't.
      challengeData = await queryClient.fetchQuery(postHostedOnboardingChallengeOptions({
        headers,
        body: {
          challengeKind
        }
      }));
    } catch (error) {
      if (getErrorCode(error) === 'E104') {
        const errorContext = (error as ApiError)?.context;
        const seconds = errorContext?.seconds ? Number(errorContext.seconds) : 0;
        initialRetryDisabledUntil = getRetryAfterDate(seconds);
      } else {
        throw error;
      }
    }
    return {
      challengeKind,
      challengeData,
      initialRetryDisabledUntil
    };
  }
});
function RegisterAuthMethodPage() {
  const $ = _c(29);
  const footprint = useFootprint();
  const {
    toast
  } = useToast();
  const {
    token
  } = Route.useParams();
  const {
    t
  } = useTranslation("idv2");
  const {
    getErrorCode,
    getErrorContext
  } = useRequestError();
  const {
    challengeKind,
    challengeData,
    initialRetryDisabledUntil: loaderRetryUntil
  } = Route.useLoaderData();
  let t0;
  if ($[0] !== challengeData || $[1] !== loaderRetryUntil) {
    t0 = () => {
      if (loaderRetryUntil) {
        return loaderRetryUntil;
      }
      return typeof challengeData?.timeBeforeRetryS === "number" ? getRetryAfterDate(challengeData.timeBeforeRetryS) : undefined;
    };
    $[0] = challengeData;
    $[1] = loaderRetryUntil;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [retryDisabledUntil, setRetryDisabledUntil] = useState(t0);
  const headers = {
    "X-Fp-Authorization": token
  };
  const verifyMutation = useMutation(postHostedOnboardingChallengeVerifyMutation({
    headers
  }));
  const resendMutation = useMutation(postHostedOnboardingChallengeMutation({
    headers
  }));
  const challengeHasError = !!resendMutation.data?.error || !!challengeData?.error;
  let t1;
  if ($[3] !== challengeHasError || $[4] !== t) {
    t1 = challengeHasError ? t(_temp) : undefined;
    $[3] = challengeHasError;
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const hint = t1;
  let t2;
  if ($[6] !== challengeKind || $[7] !== getErrorCode || $[8] !== getErrorContext || $[9] !== resendMutation || $[10] !== t || $[11] !== toast) {
    t2 = async () => {
      ;
      try {
        const data = await resendMutation.mutateAsync({
          body: {
            challengeKind
          }
        });
        setRetryDisabledUntil(getRetryAfterDate(data.timeBeforeRetryS));
        toast({
          title: t(_temp2),
          description: t(_temp3)
        });
      } catch (t3) {
        const error = t3;
        if (isSessionExpired(error)) {
          throw error;
        }
        if (getErrorCode(error) === "E104") {
          const ctx = getErrorContext(error);
          const seconds = typeof ctx?.seconds === "number" ? ctx.seconds : 0;
          setRetryDisabledUntil(getRetryAfterDate(seconds));
          toast({
            title: t(_temp4),
            description: t(_temp5),
            variant: "error"
          });
        }
      }
    };
    $[6] = challengeKind;
    $[7] = getErrorCode;
    $[8] = getErrorContext;
    $[9] = resendMutation;
    $[10] = t;
    $[11] = toast;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const handleResend = t2;
  let t3;
  if ($[13] !== footprint || $[14] !== getErrorCode || $[15] !== handleResend || $[16] !== t || $[17] !== toast || $[18] !== verifyMutation) {
    t3 = async (challengeResponse, onError) => {
      ;
      try {
        await verifyMutation.mutateAsync({
          body: {
            challengeResponse
          }
        });
        setTimeout(() => {
          footprint.navigation.forward();
        }, SUCCESS_EVENT_DELAY_MS);
      } catch (t4) {
        const error_0 = t4;
        if (isSessionExpired(error_0)) {
          throw error_0;
        }
        const code = getErrorCode(error_0);
        if (code === "E103") {
          await handleResend();
          toast({
            title: t(_temp6),
            description: t(_temp7),
            variant: "error"
          });
          return;
        }
        onError(code === "E102" ? t(_temp8) : t(_temp9));
      }
    };
    $[13] = footprint;
    $[14] = getErrorCode;
    $[15] = handleResend;
    $[16] = t;
    $[17] = toast;
    $[18] = verifyMutation;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const handleSubmit = t3;
  const t4 = challengeKind === "email" ? "email" : "sms";
  let t5;
  if ($[20] !== handleResend || $[21] !== handleSubmit || $[22] !== hint || $[23] !== resendMutation.isPending || $[24] !== retryDisabledUntil || $[25] !== t4 || $[26] !== verifyMutation.isPending || $[27] !== verifyMutation.isSuccess) {
    t5 = <OtpChallenge challengeKind={t4} hint={hint} isReturningUser={false} onSubmit={handleSubmit} isLoading={verifyMutation.isPending} isResendLoading={resendMutation.isPending} isVerifying={verifyMutation.isPending} isSuccess={verifyMutation.isSuccess} onResend={handleResend} resendDisabledUntil={retryDisabledUntil} showLegalFooter={false} />;
    $[20] = handleResend;
    $[21] = handleSubmit;
    $[22] = hint;
    $[23] = resendMutation.isPending;
    $[24] = retryDisabledUntil;
    $[25] = t4;
    $[26] = verifyMutation.isPending;
    $[27] = verifyMutation.isSuccess;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  return t5;
}
function _temp9($_7) {
  return $_7.errorFallback.genericError.subtitle;
}
function _temp8($_6) {
  return $_6.identify.otp.errors.invalidCode;
}
function _temp7($_5) {
  return $_5.identify.otp.errors.codeExpired;
}
function _temp6($_4) {
  return $_4.errorFallback.genericError.title;
}
function _temp5($_1) {
  return $_1.errorFallback.genericError.subtitle;
}
function _temp4($_0) {
  return $_0.errorFallback.genericError.title;
}
function _temp3($_3) {
  return $_3.identify.otp.resendToast.description;
}
function _temp2($_2) {
  return $_2.identify.otp.resendToast.title;
}
function _temp($) {
  return $.identify.otp.challengeTakingLonger;
}